import useStyles from 'isomorphic-style-loader/useStyles';
import { createRef, useSyncExternalStore } from 'react';
import { useIsomorphicLayoutEffect } from 'usehooks-ts';

import s from './offset-anchor.scss';

// This is a workaround to get the value of the TopNav plus the top custom banner height
// The CSS variable uses a calc which we can't access from JS

const pageOffsetAnchorRef = createRef<HTMLDivElement>();

/**
 * Reads the page offset in px from the used value of `top`, which the client
 * rect cannot give: the anchor is absolute, so its rect drifts with the scroll.
 */
export const getPageOffsetTopPx = () => {
  const anchor = pageOffsetAnchorRef.current;
  return anchor ? parseFloat(getComputedStyle(anchor).top) || 0 : 0;
};

// The offset is global and reading it forces a style recalc, so the listeners
// and the measurement are shared: N consumers must not mean N reads per event.
let offsetTopPx = 0;
const subscribers = new Set<() => void>();

const measure = () => {
  const next = getPageOffsetTopPx();
  if (next === offsetTopPx) return;

  offsetTopPx = next;
  subscribers.forEach(notify => notify());
};

const startWatching = () => {
  window.addEventListener('resize', measure);

  // The custom banner writes its height onto `:root`, with no resize event.
  const observer = new MutationObserver(measure);
  observer.observe(document.documentElement, {
    attributeFilter: ['style'],
    attributes: true,
  });

  return () => {
    window.removeEventListener('resize', measure);
    observer.disconnect();
  };
};

const subscribe = (onStoreChange: () => void) => {
  subscribers.add(onStoreChange);

  return () => {
    subscribers.delete(onStoreChange);
  };
};

const getSnapshot = () => offsetTopPx;

// Serves both the server, where the anchor cannot be measured, and disabled
// consumers; 0 is what an enabled consumer reads before the first measure.
const getZeroSnapshot = () => 0;

const noopSubscribe = () => () => {};

// To get the value of a CSS calc we have to render an element
// The className hides this element and sets the CSS variable to the element's 'top' property
export const PageOffsetAnchor = () => {
  useStyles(s);

  // Watching is tied to the anchor rather than to the subscriber count:
  // `useSyncExternalStore` subscribes in a passive effect, so a consumer
  // mounting into an unwatched store would publish a stale offset from its own
  // layout effect and correct it only after the paint.
  useIsomorphicLayoutEffect(() => {
    measure();

    return startWatching();
  }, []);

  return <div ref={pageOffsetAnchorRef} className={s.container} />;
};

export interface UseComputedPagePaddingTopPxOptions {
  /**
   * Whether to track the offset; a disabled consumer reads 0 and stays out of
   * the subscriber list, so an offset change cannot re-render it.
   */
  enabled?: boolean;
}

/**
 * The page offset in px, tracking the layout that determines it: a viewport
 * resize crossing a rem breakpoint, or a banner-height write to `:root`.
 */
export const useComputedPagePaddingTopPx = ({
  enabled = true,
}: UseComputedPagePaddingTopPxOptions = {}) =>
  useSyncExternalStore(
    enabled ? subscribe : noopSubscribe,
    enabled ? getSnapshot : getZeroSnapshot,
    getZeroSnapshot,
  );
